import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import {
  MbrsSheetData,
  RetrieveMbrsDataRequest,
  RetrieveMbrsDataResponse,
} from '../../../types/xbrl-preview';
import {
  ApiResponse,
  ConversionPhaseEnum,
  MbrsStatusEnum,
} from '../../../types/commons';
import { HttpStatus } from '../../utils/http-status.util';
import { normalizeApiErrors } from '../../utils/api-errors.util';
import {
  classifyHttpStatus,
  LoadFailureClass,
} from '../../utils/sheet-load-trace.util';
import { RuntimeConfigService } from '../common/runtime-config.service';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../../utils/http-context-tokens.util';
import { CONVERSION_DATA_UNAVAILABLE_CODES } from '../../utils/conversion-error-codes.util';

/**
 * What a `retrieve-mbrs-data` read resolves to.
 *
 * `failure` is present ONLY when the read did not succeed. Its presence is the
 * caller's signal that `sheetData` is an empty placeholder rather than a
 * conversion with no data — two states that were previously identical at the
 * call site, so a failed load ran the entire 19-sheet populate and validation
 * pipeline over `{}` on its way to redirecting the user to the dashboard.
 */
export interface RetrievedMbrsData {
  sheetData: MbrsSheetData;
  sheetStatuses?: { [slug: string]: MbrsStatusEnum };
  phase?: ConversionPhaseEnum;
  snapshot?: MbrsSheetData;
  /** Set only on failure. See {@link LoadFailureClass}. */
  failure?: LoadFailureClass;
  /** The transport status behind `failure`, for the console record. */
  statusCode?: number;
  /** The backend's stable `errorCode`, when it sent one — console-only detail. */
  errorCode?: string;
  /** Server-supplied error strings, normalised to an array. */
  errors?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class RetrieveMbrsDataService {
  private readonly config = inject(RuntimeConfigService);
  private readonly RETRIEVE_MBRS_DATA_URL = `${this.config.apiUrl()}/v2/mbrs/retrieve-mbrs-data`;

  // Custom backend-to-frontend sheet slug mapping
  private readonly SHEET_SLUG_MAP: Record<string, string> = {
    // General
    FilingInfo: 'filing-info',
    // Disclosure
    ScopeOfFiling: 'scope-of-filing',
    DirectorsRep: 'directors-report',
    DirectorsBussRev: 'director-business-review',
    StatOfDirectors: 'statement-by-directors',
    InvolveInSE: 'stock-exchange',
    AuditReport: 'auditors-report',
    // Statements
    SofpCuNonCu: 'statement-of-financial-position',
    SofpSubCuNonCu: 'sub-classification',
    SoplFunction: 'statement-of-profit-or-loss',
    SoplAnalysisFunction: 'analysis-of-profit-or-loss',
    SociNetOfTax: 'statement-of-comprehensive-income',
    SocfIndirect: 'statement-of-cash-flows-indirect',
    Socie: 'statement-of-changes-in-equity',
    // Notes
    NotesCi: 'notes-corporate-information',
    NotesSummaryofAccPol: 'notes-accounting-policies',
    NotesListofnotes: 'notes-list-of-notes',
    NotesIssuedcapital: 'notes-issued-capital',
    NotesRelatedPartytran: 'notes-related-party-transactions',
  };

  private readonly http = inject(HttpClient);

  /**
   * Retrieve MBRS data for all sheets or specific sheet.
   *
   * This service classifies a failure but deliberately does NOT navigate on
   * it — navigation is a UI decision owned by the caller, not a side effect
   * of fetching data. `FilingFormContainerComponent` is the one caller that
   * redirects on `response.failure` (`PERM` goes back to wherever the user
   * came from, `RESP` to the XBRL Files listing — see its
   * `handleContainerLoadFailure`).
   * `ConversionValidatorService.loadConversionSheetData` calls this same
   * method for its headless background validation cycle and must NOT
   * navigate on a failure there; before this split, a 403 on a conversion
   * the container was also loading could trigger the redirect TWICE — once
   * from the container's own subscription, once from the validator's —
   * sending the user back an extra page. Use `fetchFilingInfoRaw` for
   * fire-and-forget reads that don't even need classification.
   * @param conversionId - Conversion ID
   * @param sheet - Sheet ID (empty string for all sheets)
   * @returns Observable with sheet data and sheetStatuses from the API
   */
  fetchMbrsData(
    conversionId: string,
    sheet: string = ''
  ): Observable<RetrievedMbrsData> {
    const body: RetrieveMbrsDataRequest = {
      ConversionId: conversionId,
      Sheet: sheet,
    };

    return this.http
      .post<ApiResponse<RetrieveMbrsDataResponse>>(this.RETRIEVE_MBRS_DATA_URL, body, {
        withCredentials: true,
        // See SKIP_GLOBAL_ERROR_REDIRECT's own docblock: this call's failure
        // is classified and toasted by SheetLoadTraceService (the container
        // scope) — the global interceptor's redirect must not win that race.
        context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true),
      })
      .pipe(
        catchError(this.handleMbrsDataError),
        map((response) => {
          if (!response.status) {
            // Classify so the caller can report WHY the load failed and
            // decide for itself whether/where to navigate. This branch is
            // reached by every transport failure too — `handleMbrsDataError`
            // turns them into a `status: false` envelope rather than an error
            // emission, which is why the container's `error` callback (and
            // its "Failed to load MBRS data" toast) never fired for the
            // network outage it was written for. The classified `failure` is
            // that lost signal, restored.
            const errors = normalizeApiErrors(response.errors);
            // This ONE endpoint's ownership check answers with HTTP 400 — not
            // 403 — carrying "Conversion not belongs to current user!" in
            // `errors`, which `classifyHttpStatus` would otherwise read as a
            // generic `RESP` (rejected request). `RESP` sends the user to the
            // XBRL Files listing instead of `PERM`'s "go back to where you came
            // from" — the exact bug this override exists to close. Message-matching is
            // the only option here: the backend sends no `errorCode` for this
            // response, so there is nothing more stable to key on. Scoped to
            // this one known phrase — do not widen it into a generic
            // "any 400 might really be a 403" rule, which would misclassify
            // every genuine validation-style 400 this endpoint can also send.
            // The conversion EXISTS but its data cannot be served (still being
            // written, none for the sheet, or none at all). The backend sends
            // these as 404/409 with their own `errorCode`; the status alone
            // would read as `RESP` ("no such conversion") and redirect the user
            // away with a false message. Key on the code first. They land on
            // `SRV` — stay on the page, detail in the console — with the code
            // in the console record telling the three apart.
            const failure = this.isOwnershipDenialMessage(errors)
              ? 'PERM'
              : CONVERSION_DATA_UNAVAILABLE_CODES.has(response.errorCode ?? '')
                ? 'SRV'
                : classifyHttpStatus(response.statusCode);
            return {
              sheetData: {},
              sheetStatuses: undefined,
              phase: undefined,
              snapshot: undefined,
              failure,
              statusCode: response.statusCode,
              errorCode: response.errorCode,
              errors,
            };
          }

          const responseData = response.data || {};

          // Extract sheetStatuses + phase BEFORE transforming sheet keys — both
          // are sibling metadata fields on the response, not sheet payloads.
          const sheetStatuses = responseData.sheetStatuses as { [slug: string]: MbrsStatusEnum } | undefined;
          const phase = responseData.phase as ConversionPhaseEnum | undefined;
          const snapshotRaw = responseData.snapshot as MbrsSheetData | undefined;

          // Strip metadata + snapshot so the remainder is purely per-sheet payloads.
          // Without removing `snapshot` here it would be transformed into a fake sheet.
          const {
            sheetStatuses: _s,
            phase: _p,
            snapshot: _snap,
            ...sheetDataRaw
          } = responseData;

          const sheetData = this.sanitizeNullStrings(
            this.transformSheetKeys(sheetDataRaw as MbrsSheetData)
          );
          // The snapshot has the SAME slug→payload shape as the per-sheet data, so it goes
          // through the identical slug-mapping + null-sanitisation pipeline.
          const snapshot = snapshotRaw
            ? this.sanitizeNullStrings(this.transformSheetKeys(snapshotRaw))
            : undefined;

          return { sheetData, sheetStatuses, phase, snapshot };
        })
      );
  }

  /**
   * Lightweight read of a single sheet's data — no redirect, no navigation side effects.
   * Returns the raw filing-info key/value map, or an empty object on failure.
   * Used by developer tooling (debug layouts) that bulk-fetches data and
   * expects some conversions to legitimately have no MBRS data yet.
   */
  fetchSheetDataRaw(
    conversionId: string,
    sheet: string,
  ): Observable<Record<string, string | unknown>> {
    const body: RetrieveMbrsDataRequest = {
      ConversionId: conversionId,
      Sheet: sheet,
    };

    return this.http
      .post<ApiResponse<RetrieveMbrsDataResponse>>(this.RETRIEVE_MBRS_DATA_URL, body, {
        withCredentials: true,
      })
      .pipe(
        catchError(this.handleMbrsDataError),
        map((response) => {
          if (!response.status) return {};

          const responseData = response.data || {};
          const {
            sheetStatuses: _,
            snapshot: _snap,
            ...sheetDataRaw
          } = responseData;
          const transformed = this.transformSheetKeys(sheetDataRaw as MbrsSheetData);
          const sanitized = this.sanitizeNullStrings(transformed);

          // Return the requested sheet's data.
          // When sheet is empty the API returns all sheets — default to filing-info.
          const slug = sheet ? (this.SHEET_SLUG_MAP[sheet] || sheet) : 'filing-info';
          const fallbackKey = sheet || 'FilingInfo';
          return (sanitized[slug] ?? sanitized[fallbackKey] ?? {}) as Record<string, string | unknown>;
        }),
      );
  }

  /**
   * Does this `retrieve-mbrs-data` error body actually mean "not yours to
   * see", despite arriving on a 400 rather than a 403?
   *
   * Confirmed from a live payload (2026-10-05):
   * `{ status: false, statusCode: 400, errors: "Conversion not belongs to
   * current user!" }`. Matched case-insensitively and by substring rather
   * than an exact string, since the trailing "!" and casing are not a
   * contract — only the phrase itself is confirmed stable.
   */
  private isOwnershipDenialMessage(errors: readonly string[]): boolean {
    return errors.some((error) =>
      error.toLowerCase().includes('not belongs to current user')
    );
  }

  /**
   * Transform backend PascalCase keys to frontend kebab-case keys
   */
  private transformSheetKeys(data: MbrsSheetData): MbrsSheetData {
    const transformed: MbrsSheetData = {};

    for (const [backendKey, value] of Object.entries(data)) {
      // Use custom mapping if defined, otherwise keep original key
      const frontendKey = this.SHEET_SLUG_MAP[backendKey] || backendKey;
      transformed[frontendKey] = value;
    }

    return transformed;
  }

  /**
   * Recursively convert string "null" to actual null throughout the data structure
   * Handles nested objects and arrays
   */
  private sanitizeNullStrings(data: any): any {
    // Handle null or undefined
    if (data === null || data === undefined) {
      return data;
    }

    // Convert string "null" to actual null
    if (data === 'null') {
      return null;
    }

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map((item) => this.sanitizeNullStrings(item));
    }

    // Handle objects
    if (typeof data === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(data)) {
        sanitized[key] = this.sanitizeNullStrings(value);
      }
      return sanitized;
    }

    // Return primitive values as-is
    return data;
  }

  /**
   * Convert a transport failure into a `status: false` envelope so downstream
   * `map` operators see one uniform shape.
   *
   * ## The status code must survive
   *
   * `error.status` is preserved VERBATIM, including `0`. That zero is Angular's
   * `HttpErrorResponse` sentinel for "the request never reached the server", and
   * it is the only evidence that a failure was a network outage rather than a
   * server fault. An earlier `error.status || HttpStatus.InternalServerError`
   * here silently rewrote every offline load into a 500, which made the two
   * indistinguishable in exactly the reports we were being asked to tell apart.
   * `??` — not `||` — is load-bearing for that reason.
   *
   * The same applies to the body-carrying branch: a server-supplied error body
   * may omit `statusCode`, so the transport status is filled in behind it rather
   * than the body being passed through untouched.
   */
  private handleMbrsDataError(
    error: HttpErrorResponse
  ): Observable<ApiResponse<RetrieveMbrsDataResponse>> {
    if (error.error && error.error.errors) {
      return of({
        ...error.error,
        status: false,
        statusCode: error.error.statusCode ?? error.status,
      });
    }

    return of({
      status: false,
      statusCode: error.status ?? HttpStatus.InternalServerError,
      data: {},
      errors: [error.message || 'Failed to retrieve MBRS data'],
    });
  }
}
